/**
 * Algolia Discount Checker
 *
 * Post-render enhancement for Algolia PLP/search pages.
 * Creates a temporary Storefront API cart (not the customer's real cart)
 * with all visible variant IDs to detect automatic discounts via
 * discountAllocations. Stores discount data globally and dispatches
 * events so preact-managed ProductCard components can render prices
 * without DOM conflicts.
 *
 * Reads variant IDs directly from Algolia's search response
 * (helper.lastResults.hits) to avoid DOM dependency and eliminate
 * the 150ms debounce delay.
 */
(function () {
  'use strict';

  const STOREFRONT_TOKEN = 'f03bcbe287205c23fd45fdd8609eefb1';
  const API_VERSION = '2025-01';
  const CACHE_KEY = 'algolia_discount_cache';
  const checkedVariants = new Set();

  // Global discount store — read by preact ProductCard components
  // Pre-populate from sessionStorage so returning visitors see discounts on first render
  if (!window.__algoliaDiscounts) {
    window.__algoliaDiscounts = new Map();
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        for (const [id, info] of Object.entries(cached)) {
          window.__algoliaDiscounts.set(String(id), info);
        }
      }
    } catch {}
  }

  // Signals that the first discount check is complete — ProductCard shows
  // a price skeleton until this is true so prices don't flash/shift.
  // If sessionStorage had cached data, mark ready immediately.
  if (typeof window.__discountsReady === 'undefined') {
    window.__discountsReady = window.__algoliaDiscounts.size > 0;
  }

  // ---------------------------------------------------------------------------
  // Session storage cache
  // ---------------------------------------------------------------------------

  function getCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  function setCache(variantId, data) {
    try {
      const cache = getCache();
      cache[variantId] = data;
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch {
      // sessionStorage full or unavailable
    }
  }

  function getCached(variantId) {
    const cache = getCache();
    return cache[variantId] !== undefined ? cache[variantId] : null;
  }

  // ---------------------------------------------------------------------------
  // Variant ID extraction
  // ---------------------------------------------------------------------------

  function extractVariantIdsFromHits() {
    try {
      const hits = window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.hits;
      if (!hits?.length) return null;
      const ids = [];
      for (const hit of hits) {
        const raw = hit.variant_id || hit.variantId || hit.variants?.[0]?.id || hit.objectID;
        if (raw != null) ids.push(String(raw));
      }
      return ids.length > 0 ? ids : null;
    } catch {
      return null;
    }
  }

  function extractVariantIdsFromDOM() {
    const btns = document.querySelectorAll('.product-add-cart[data-variant-id]');
    if (btns.length === 0) return null;
    const ids = [];
    for (const btn of btns) {
      const id = btn.getAttribute('data-variant-id');
      if (id) ids.push(id);
    }
    return ids.length > 0 ? ids : null;
  }

  // ---------------------------------------------------------------------------
  // Storefront API cartCreate
  // ---------------------------------------------------------------------------

  const CART_CREATE_QUERY = `
    mutation CartCreate($lines: [CartLineInput!]!) {
      cartCreate(input: { lines: $lines }) {
        cart {
          lines(first: 250) {
            edges {
              node {
                merchandise {
                  ... on ProductVariant { id }
                }
                cost {
                  amountPerQuantity { amount }
                  compareAtAmountPerQuantity { amount }
                  totalAmount { amount }
                }
                discountAllocations {
                  discountedAmount { amount }
                  ... on CartAutomaticDiscountAllocation { title }
                  ... on CartCodeDiscountAllocation { code }
                }
              }
            }
          }
        }
        userErrors { field message }
      }
    }
  `;

  function extractVariantNumericId(gid) {
    const match = gid.match(/ProductVariant\/(\d+)/);
    return match ? match[1] : null;
  }

  function parseCartEdges(edges) {
    const results = new Map();
    for (const edge of edges) {
      const node = edge.node;
      const variantGid = node.merchandise?.id;
      if (!variantGid) continue;

      const numericId = extractVariantNumericId(variantGid);
      if (!numericId) continue;

      const allocations = node.discountAllocations || [];
      const hasDiscount = allocations.length > 0 && allocations.some(a => parseFloat(a.discountedAmount?.amount || 0) > 0);

      if (hasDiscount) {
        const originalPrice = parseFloat(node.cost?.amountPerQuantity?.amount || 0);
        const totalDiscount = allocations.reduce((sum, a) => sum + parseFloat(a.discountedAmount?.amount || 0), 0);
        const finalPrice = originalPrice - totalDiscount;
        const title = allocations.map(a => a.title || a.code || '').filter(Boolean).join(', ');

        results.set(numericId, {
          hasDiscount: true,
          discountTitle: title,
          originalPrice,
          finalPrice: Math.max(finalPrice, 0),
        });
      } else {
        results.set(numericId, { hasDiscount: false });
      }
    }
    return results;
  }

  async function fetchDiscountsViaCart(variantIds) {
    const lines = variantIds.map(id => ({
      merchandiseId: `gid://shopify/ProductVariant/${id}`,
      quantity: 1,
    }));

    try {
      const res = await fetch(`/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
        },
        body: JSON.stringify({ query: CART_CREATE_QUERY, variables: { lines } }),
      });

      const json = await res.json();
      if (json.errors) {
        console.error('[algolia-discount-checker] Storefront API errors:', json.errors);
        return new Map();
      }

      const userErrors = json?.data?.cartCreate?.userErrors || [];
      const edges = json?.data?.cartCreate?.cart?.lines?.edges || [];

      // If userErrors exist, the cart was not created — retry without invalid IDs
      if (userErrors.length > 0 && edges.length === 0) {
        const badIds = new Set();
        for (const err of userErrors) {
          const idx = err.field?.find(f => /^\d+$/.test(f));
          if (idx != null && lines[idx]) {
            const gid = lines[idx].merchandiseId;
            const num = extractVariantNumericId(gid);
            if (num) badIds.add(num);
          }
        }

        if (badIds.size > 0) {
          const cleanIds = variantIds.filter(id => !badIds.has(id));
          if (cleanIds.length > 0) {
            return fetchDiscountsViaCart(cleanIds);
          }
        }
        return new Map();
      }

      return parseCartEdges(edges);
    } catch (err) {
      console.error('[algolia-discount-checker] Fetch error:', err);
      return new Map();
    }
  }

  // ---------------------------------------------------------------------------
  // Publish discount data (consumed by preact ProductCard via state)
  // ---------------------------------------------------------------------------

  function publishDiscount(variantId, discountInfo) {
    window.__algoliaDiscounts.set(String(variantId), discountInfo);
    document.dispatchEvent(new CustomEvent('algolia.discount.resolved', {
      detail: { variantId: String(variantId), discountInfo },
    }));
  }

  // ---------------------------------------------------------------------------
  // Main processing
  // ---------------------------------------------------------------------------

  async function processNewHits() {
    // Primary: read variant IDs from Algolia hits (no DOM dependency)
    let variantIds = extractVariantIdsFromHits();

    // Fallback: DOM query if helper.lastResults unavailable
    if (variantIds === null) {
      variantIds = extractVariantIdsFromDOM();
    }

    if (!variantIds || variantIds.length === 0) return;

    // Filter already-checked variants
    const newIds = variantIds.filter(id => !checkedVariants.has(id));
    if (newIds.length === 0) return;

    // Mark checked immediately to prevent duplicate processing
    for (const id of newIds) checkedVariants.add(id);

    // Separate cached from uncached
    const uncachedIds = [];
    for (const id of newIds) {
      const cached = getCached(id);
      if (cached !== null) {
        publishDiscount(id, cached);
      } else {
        uncachedIds.push(id);
      }
    }

    if (uncachedIds.length === 0) return;

    // Check each variant individually (1 item per cart) so discounts don't
    // interfere with each other — matches PDP behavior exactly.
    const results = await Promise.all(uncachedIds.map(id =>
      fetchDiscountsViaCart([id]).then(map => ({ id, info: map.get(id) || { hasDiscount: false } }))
    ));

    for (const { id, info } of results) {
      setCache(id, info);
      publishDiscount(id, info);
    }
  }

  // ---------------------------------------------------------------------------
  // Event hook
  // ---------------------------------------------------------------------------

  let _processing = false;
  let _pendingRerun = false;

  document.addEventListener('algolia.instantSearch.onRender', () => {
    if (_processing) {
      _pendingRerun = true;
      return;
    }
    runProcessing();
  });

  async function runProcessing() {
    _processing = true;
    try {
      await processNewHits();
    } finally {
      _processing = false;

      // Signal that discount data is available for rendering
      if (!window.__discountsReady) {
        window.__discountsReady = true;
        document.dispatchEvent(new CustomEvent('algolia.discounts.ready'));
      }

      if (_pendingRerun) {
        _pendingRerun = false;
        runProcessing();
      }
    }
  }
})();
